/*
 * The number input of the app with its editing logic: formatting while
 * typing, the caret, spinning.
 */
// Based on the InputNumber of PrimeNG 14 (MIT), see tools/licenses/primeng.txt.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  type SimpleChanges,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** The value while typing and the formatted text of it. */
export interface NumberInputEvent {
  originalEvent: Event;
  value: number | null;
  formattedValue: string;
}

@Component({
  selector: 'app-number-input',
  template: `<span
      class="app-number-input {{ styleClass }}"
      [ngClass]="{
        'app-number-input-buttons-stacked':
          showButtons && buttonLayout === 'stacked',
        'app-number-input-buttons-horizontal':
          showButtons && buttonLayout === 'horizontal',
      }"
    >
      <input
        #input
        class="app-input app-number-input-field"
        type="text"
        inputmode="decimal"
        [value]="formattedValue()"
        [attr.placeholder]="placeholder"
        [attr.id]="inputId"
        [attr.aria-label]="ariaLabel"
        [attr.min]="min"
        [attr.max]="max"
        [disabled]="disabled"
        [readonly]="readonly"
        (input)="onUserInput($event)"
        (keydown)="onInputKeyDown($event)"
        (keypress)="onInputKeyPress($event)"
        (paste)="onPaste($event)"
        (click)="onInputClick()"
        (focus)="focused = true"
        (blur)="onInputBlur($event)"
      />
      @if (showClear && value) {
        <i
          class="app-number-input-clear app-icon fa-solid fa-xmark"
          role="button"
          tabindex="0"
          aria-label="Clear"
          (click)="clear()"
          (keydown.enter)="clear()"
          (keydown.space)="$event.preventDefault(); clear()"
        ></i>
      }
      @if (showButtons && buttonLayout === 'stacked') {
        <span class="app-number-input-button-group">
          <ng-container
            *ngTemplateOutlet="spinButton; context: { dir: 1 }"
          ></ng-container>
          <ng-container
            *ngTemplateOutlet="spinButton; context: { dir: -1 }"
          ></ng-container>
        </span>
      }
      @if (showButtons && buttonLayout === 'horizontal') {
        <ng-container
          *ngTemplateOutlet="spinButton; context: { dir: 1 }"
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="spinButton; context: { dir: -1 }"
        ></ng-container>
      }
    </span>
    <ng-template #spinButton let-dir="dir">
      <button
        type="button"
        class="app-number-input-button"
        [class.app-number-input-button-up]="dir > 0"
        [class.app-number-input-button-down]="dir < 0"
        [disabled]="disabled"
        (mousedown)="onSpinMouseDown($event, dir)"
        (mouseup)="clearTimer()"
        (mouseleave)="clearTimer()"
        (keydown)="onSpinKeyDown($event, dir)"
        (keyup)="clearTimer()"
      >
        <span
          class="app-number-input-button-icon"
          [class]="dir > 0 ? incrementButtonIcon : decrementButtonIcon"
        ></span>
      </button>
    </ng-template>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, NgTemplateOutlet],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
  ],
})
export class NumberInputComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  private cd = inject(ChangeDetectorRef);

  @Input() placeholder: string | null = null;
  @Input() inputId: string | null = null;
  @Input() ariaLabel: string | null = null;
  @Input() styleClass = '';
  @Input() locale: string | undefined;
  @Input() useGrouping = true;
  @Input() minFractionDigits: number | undefined;
  @Input() maxFractionDigits: number | undefined;
  @Input() prefix: string | undefined;
  @Input() suffix: string | undefined;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step = 1;
  @Input() showClear = false;
  @Input() showButtons = false;
  @Input() buttonLayout: 'stacked' | 'horizontal' = 'stacked';
  @Input() incrementButtonIcon = 'app-icon fa-solid fa-angle-up';
  @Input() decrementButtonIcon = 'app-icon fa-solid fa-angle-down';
  @Input() readonly = false;
  /**
   * A comma typed is the decimal point, in number boxes without format: "1,5"
   * gives 1.5. The locale en-GB would take it for the grouping separator and
   * drop it.
   */
  @Input() decimalComma = false;
  @Input() disabled = false;

  /** Every change of the value while typing. */
  @Output() valueInput = new EventEmitter<NumberInputEvent>();

  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;

  @HostBinding('class.app-number-input-clearable') get clearable() {
    return this.showClear;
  }
  @HostBinding('class.app-inputwrapper-filled') get filled() {
    return this.value != null && this.value.toString().length > 0;
  }
  @HostBinding('class.app-inputwrapper-focus') focused = false;

  value: any = null;

  private onModelChange: (value: any) => void = () => undefined;
  private onModelTouched: () => void = () => undefined;
  private initialized = false;
  private timer: any;
  private lastValue: string | undefined;
  private isSpecialChar = false;
  private groupChar = '';
  private prefixChar = '';
  private suffixChar = '';
  private numberFormat!: Intl.NumberFormat;
  private _numeral!: RegExp;
  private _group!: RegExp;
  private _minusSign!: RegExp;
  private _currency!: RegExp;
  private _decimal!: RegExp;
  private _suffix!: RegExp;
  private _prefix!: RegExp;
  private _index!: (d: string) => number | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    const props = [
      'locale',
      'useGrouping',
      'minFractionDigits',
      'maxFractionDigits',
      'prefix',
      'suffix',
    ];
    if (props.some((p) => !!changes[p]) && this.initialized) {
      this.constructParser();
    }
  }

  ngOnInit(): void {
    this.constructParser();
    this.initialized = true;
  }

  private getOptions(): Intl.NumberFormatOptions {
    return {
      style: 'decimal',
      useGrouping: this.useGrouping,
      minimumFractionDigits: this.minFractionDigits,
      maximumFractionDigits: this.maxFractionDigits,
    };
  }

  private constructParser() {
    this.numberFormat = new Intl.NumberFormat(this.locale, this.getOptions());
    const numerals = [
      ...new Intl.NumberFormat(this.locale, { useGrouping: false }).format(
        9876543210
      ),
    ].reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));
    this._numeral = new RegExp(`[${numerals.join('')}]`, 'g');
    this._group = this.getGroupingExpression();
    this._minusSign = this.getMinusSignExpression();
    this._currency = new RegExp(`[]`, 'g');
    this._decimal = this.getDecimalExpression();
    this._suffix = this.getSuffixExpression();
    this._prefix = this.getPrefixExpression();
    this._index = (d: string) => index.get(d);
  }

  private escapeRegExp(text: string) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  private getDecimalExpression() {
    const formatter = new Intl.NumberFormat(this.locale, {
      ...this.getOptions(),
      useGrouping: false,
    });
    return new RegExp(
      `[${formatter
        .format(1.1)
        .replace(this._currency, '')
        .trim()
        .replace(this._numeral, '')}]`,
      'g'
    );
  }

  private getGroupingExpression() {
    const formatter = new Intl.NumberFormat(this.locale, { useGrouping: true });
    this.groupChar = formatter
      .format(1000000)
      .trim()
      .replace(this._numeral, '')
      .charAt(0);
    return new RegExp(`[${this.groupChar}]`, 'g');
  }

  private getMinusSignExpression() {
    const formatter = new Intl.NumberFormat(this.locale, {
      useGrouping: false,
    });
    return new RegExp(
      `[${formatter.format(-1).trim().replace(this._numeral, '')}]`,
      'g'
    );
  }

  private getPrefixExpression() {
    if (this.prefix) {
      this.prefixChar = this.prefix;
    } else {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: 'decimal',
      });
      this.prefixChar = formatter.format(1).split('1')[0];
    }
    return new RegExp(`${this.escapeRegExp(this.prefixChar || '')}`, 'g');
  }

  private getSuffixExpression() {
    if (this.suffix) {
      this.suffixChar = this.suffix;
    } else {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      this.suffixChar = formatter.format(1).split('1')[1];
    }
    return new RegExp(`${this.escapeRegExp(this.suffixChar || '')}`, 'g');
  }

  formatValue(value: any): string {
    if (value != null) {
      if (value === '-') {
        return value;
      }
      let formattedValue = new Intl.NumberFormat(
        this.locale,
        this.getOptions()
      ).format(value);
      if (this.prefix) {
        formattedValue = this.prefix + formattedValue;
      }
      if (this.suffix) {
        formattedValue = formattedValue + this.suffix;
      }
      return formattedValue;
    }
    return '';
  }

  parseValue(text: string): any {
    const filteredText = text
      .replace(this._suffix, '')
      .replace(this._prefix, '')
      .trim()
      .replace(/\s/g, '')
      .replace(this._currency, '')
      .replace(this._group, '')
      .replace(this._minusSign, '-')
      .replace(this._decimal, '.')
      .replace(this._numeral, this._index as any);
    if (filteredText) {
      if (filteredText === '-') {
        return filteredText;
      }
      const parsedValue = +filteredText;
      return isNaN(parsedValue) ? null : parsedValue;
    }
    return null;
  }

  private repeat(event: Event, interval: number | null, dir: number) {
    if (this.readonly) {
      return;
    }
    const i = interval || 500;
    this.clearTimer();
    this.timer = setTimeout(() => this.repeat(event, 40, dir), i);
    this.spin(event, dir);
  }

  private spin(event: Event, dir: number) {
    const step = this.step * dir;
    const currentValue = this.parseValue(this.input.nativeElement.value) || 0;
    const newValue = this.validateValue(currentValue + step);
    this.updateInput(newValue, null, 'spin', null);
    this.updateModel(event, newValue);
    this.handleOnInput(event, currentValue, newValue);
    // The held spinner buttons repeat from a timer.
    this.cd.markForCheck();
  }

  clear() {
    this.value = null;
    this.onModelChange(this.value);
  }

  onSpinMouseDown(event: MouseEvent, dir: number) {
    this.input.nativeElement.focus();
    this.repeat(event, null, dir);
    event.preventDefault();
  }

  onSpinKeyDown(event: KeyboardEvent, dir: number) {
    if (event.key === ' ' || event.key === 'Enter') {
      this.repeat(event, null, dir);
    }
  }

  onUserInput(event: Event) {
    if (this.readonly) {
      return;
    }
    if (this.isSpecialChar) {
      (event.target as HTMLInputElement).value = this.lastValue as string;
    }
    this.isSpecialChar = false;
  }

  onInputKeyDown(event: KeyboardEvent) {
    if (this.readonly) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.lastValue = target.value;
    if (event.shiftKey || event.altKey) {
      this.isSpecialChar = true;
      return;
    }
    const selectionStart = target.selectionStart as number;
    const selectionEnd = target.selectionEnd as number;
    const inputValue = target.value;
    let newValueStr: any = null;

    switch (event.key) {
      case 'ArrowUp':
        this.spin(event, 1);
        event.preventDefault();
        break;
      case 'ArrowDown':
        this.spin(event, -1);
        event.preventDefault();
        break;
      case 'ArrowLeft':
        if (!this.isNumeralChar(inputValue.charAt(selectionStart - 1))) {
          event.preventDefault();
        }
        break;
      case 'ArrowRight':
        if (!this.isNumeralChar(inputValue.charAt(selectionStart))) {
          event.preventDefault();
        }
        break;
      case 'Enter':
        newValueStr = this.validateValue(
          this.parseValue(this.input.nativeElement.value)
        );
        this.input.nativeElement.value = this.formatValue(newValueStr);
        this.updateModel(event, newValueStr);
        break;
      case 'Backspace': {
        event.preventDefault();
        if (selectionStart === selectionEnd) {
          const deleteChar = inputValue.charAt(selectionStart - 1);
          const { decimalCharIndex, decimalCharIndexWithoutPrefix } =
            this.getDecimalCharIndexes(inputValue);
          if (this.isNumeralChar(deleteChar)) {
            const decimalLength = this.getDecimalLength(inputValue);
            if (this._group.test(deleteChar)) {
              this._group.lastIndex = 0;
              newValueStr =
                inputValue.slice(0, selectionStart - 2) +
                inputValue.slice(selectionStart - 1);
            } else if (this._decimal.test(deleteChar)) {
              this._decimal.lastIndex = 0;
              if (decimalLength) {
                this.input.nativeElement.setSelectionRange(
                  selectionStart - 1,
                  selectionStart - 1
                );
              } else {
                newValueStr =
                  inputValue.slice(0, selectionStart - 1) +
                  inputValue.slice(selectionStart);
              }
            } else if (
              decimalCharIndex > 0 &&
              selectionStart > decimalCharIndex
            ) {
              const insertedText =
                (this.minFractionDigits || 0) < decimalLength ? '' : '0';
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                insertedText +
                inputValue.slice(selectionStart);
            } else if (decimalCharIndexWithoutPrefix === 1) {
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                '0' +
                inputValue.slice(selectionStart);
              newValueStr = this.parseValue(newValueStr) > 0 ? newValueStr : '';
            } else {
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                inputValue.slice(selectionStart);
            }
          }
          this.updateValue(event, newValueStr, null, 'delete-single');
        } else {
          newValueStr = this.deleteRange(
            inputValue,
            selectionStart,
            selectionEnd
          );
          this.updateValue(event, newValueStr, null, 'delete-range');
        }
        break;
      }
      case 'Delete':
        event.preventDefault();
        if (selectionStart === selectionEnd) {
          const deleteChar = inputValue.charAt(selectionStart);
          const { decimalCharIndex, decimalCharIndexWithoutPrefix } =
            this.getDecimalCharIndexes(inputValue);
          if (this.isNumeralChar(deleteChar)) {
            const decimalLength = this.getDecimalLength(inputValue);
            if (this._group.test(deleteChar)) {
              this._group.lastIndex = 0;
              newValueStr =
                inputValue.slice(0, selectionStart) +
                inputValue.slice(selectionStart + 2);
            } else if (this._decimal.test(deleteChar)) {
              this._decimal.lastIndex = 0;
              if (decimalLength) {
                this.input.nativeElement.setSelectionRange(
                  selectionStart + 1,
                  selectionStart + 1
                );
              } else {
                newValueStr =
                  inputValue.slice(0, selectionStart) +
                  inputValue.slice(selectionStart + 1);
              }
            } else if (
              decimalCharIndex > 0 &&
              selectionStart > decimalCharIndex
            ) {
              const insertedText =
                (this.minFractionDigits || 0) < decimalLength ? '' : '0';
              newValueStr =
                inputValue.slice(0, selectionStart) +
                insertedText +
                inputValue.slice(selectionStart + 1);
            } else if (decimalCharIndexWithoutPrefix === 1) {
              newValueStr =
                inputValue.slice(0, selectionStart) +
                '0' +
                inputValue.slice(selectionStart + 1);
              newValueStr = this.parseValue(newValueStr) > 0 ? newValueStr : '';
            } else {
              newValueStr =
                inputValue.slice(0, selectionStart) +
                inputValue.slice(selectionStart + 1);
            }
          }
          this.updateValue(event, newValueStr, null, 'delete-back-single');
        } else {
          newValueStr = this.deleteRange(
            inputValue,
            selectionStart,
            selectionEnd
          );
          this.updateValue(event, newValueStr, null, 'delete-range');
        }
        break;
      default:
        break;
    }
  }

  onInputKeyPress(event: KeyboardEvent) {
    if (this.readonly) {
      return;
    }
    let char = event.key;
    if (char !== 'Enter') {
      event.preventDefault();
    }
    if (char.length !== 1) {
      return;
    }
    if (this.decimalComma && char === ',') {
      char = '.';
    }
    const isDecimalSign =
      this.decimalComma && char === '.' ? true : this.isDecimalSign(char);
    const isMinusSign = this.isMinusSign(char);
    if (/[0-9]/.test(char) || isMinusSign || isDecimalSign) {
      this.insert(event, char, { isDecimalSign, isMinusSign });
    }
  }

  onPaste(event: ClipboardEvent) {
    if (!this.disabled && !this.readonly) {
      event.preventDefault();
      const data = event.clipboardData?.getData('Text');
      if (data) {
        const filteredData = this.parseValue(data);
        if (filteredData != null) {
          this.insert(event, filteredData.toString());
        }
      }
    }
  }

  private allowMinusSign() {
    return this.min == null || this.min < 0;
  }

  private isMinusSign(char: string) {
    if (this._minusSign.test(char) || char === '-') {
      this._minusSign.lastIndex = 0;
      return true;
    }
    return false;
  }

  private isDecimalSign(char: string) {
    if (this._decimal.test(char)) {
      this._decimal.lastIndex = 0;
      return true;
    }
    return false;
  }

  private getDecimalCharIndexes(val: string) {
    const decimalCharIndex = val.search(this._decimal);
    this._decimal.lastIndex = 0;
    const filteredVal = val
      .replace(this._prefix, '')
      .trim()
      .replace(/\s/g, '')
      .replace(this._currency, '');
    const decimalCharIndexWithoutPrefix = filteredVal.search(this._decimal);
    this._decimal.lastIndex = 0;
    return { decimalCharIndex, decimalCharIndexWithoutPrefix };
  }

  private getCharIndexes(val: string) {
    const decimalCharIndex = val.search(this._decimal);
    this._decimal.lastIndex = 0;
    const minusCharIndex = val.search(this._minusSign);
    this._minusSign.lastIndex = 0;
    const suffixCharIndex = val.search(this._suffix);
    this._suffix.lastIndex = 0;
    const currencyCharIndex = val.search(this._currency);
    this._currency.lastIndex = 0;
    return {
      decimalCharIndex,
      minusCharIndex,
      suffixCharIndex,
      currencyCharIndex,
    };
  }

  private insert(
    event: Event,
    text: string,
    sign = { isDecimalSign: false, isMinusSign: false }
  ) {
    const minusCharIndexOnText = text.search(this._minusSign);
    this._minusSign.lastIndex = 0;
    if (!this.allowMinusSign() && minusCharIndexOnText !== -1) {
      return;
    }
    const selectionStart = this.input.nativeElement.selectionStart as number;
    const selectionEnd = this.input.nativeElement.selectionEnd as number;
    const inputValue = this.input.nativeElement.value.trim();
    const {
      decimalCharIndex,
      minusCharIndex,
      suffixCharIndex,
      currencyCharIndex,
    } = this.getCharIndexes(inputValue);
    let newValueStr;
    if (sign.isMinusSign) {
      if (selectionStart === 0) {
        newValueStr = inputValue;
        if (minusCharIndex === -1 || selectionEnd !== 0) {
          newValueStr = this.insertText(inputValue, text, 0, selectionEnd);
        }
        this.updateValue(event, newValueStr, text, 'insert');
      }
    } else if (sign.isDecimalSign) {
      if (decimalCharIndex > 0 && selectionStart === decimalCharIndex) {
        this.updateValue(event, inputValue, text, 'insert');
      } else if (
        decimalCharIndex > selectionStart &&
        decimalCharIndex < selectionEnd
      ) {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, 'insert');
      } else if (decimalCharIndex === -1 && this.maxFractionDigits) {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, 'insert');
      }
    } else {
      const maxFractionDigits = this.numberFormat.resolvedOptions()
        .maximumFractionDigits as number;
      const operation =
        selectionStart !== selectionEnd ? 'range-insert' : 'insert';
      if (decimalCharIndex > 0 && selectionStart > decimalCharIndex) {
        if (
          selectionStart + text.length - (decimalCharIndex + 1) <=
          maxFractionDigits
        ) {
          const charIndex =
            currencyCharIndex >= selectionStart
              ? currencyCharIndex - 1
              : suffixCharIndex >= selectionStart
                ? suffixCharIndex
                : inputValue.length;
          newValueStr =
            inputValue.slice(0, selectionStart) +
            text +
            inputValue.slice(selectionStart + text.length, charIndex) +
            inputValue.slice(charIndex);
          this.updateValue(event, newValueStr, text, operation);
        }
      } else {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, operation);
      }
    }
  }

  private insertText(value: string, text: string, start: number, end: number) {
    const textSplit = text === '.' ? text : text.split('.');
    if (textSplit.length === 2) {
      const decimalCharIndex = value.slice(start, end).search(this._decimal);
      this._decimal.lastIndex = 0;
      return decimalCharIndex > 0
        ? value.slice(0, start) + this.formatValue(text) + value.slice(end)
        : value || this.formatValue(text);
    } else if (end - start === value.length) {
      return this.formatValue(text);
    } else if (start === 0) {
      return text + value.slice(end);
    } else if (end === value.length) {
      return value.slice(0, start) + text;
    } else {
      return value.slice(0, start) + text + value.slice(end);
    }
  }

  private deleteRange(value: string, start: number, end: number) {
    if (end - start === value.length) return '';
    if (start === 0) return value.slice(end);
    if (end === value.length) return value.slice(0, start);
    return value.slice(0, start) + value.slice(end);
  }

  private initCursor() {
    let selectionStart = this.input.nativeElement.selectionStart as number;
    let inputValue = this.input.nativeElement.value;
    const valueLength = inputValue.length;
    let index: number | null = null;
    const prefixLength = (this.prefixChar || '').length;
    inputValue = inputValue.replace(this._prefix, '');
    selectionStart = selectionStart - prefixLength;
    let char = inputValue.charAt(selectionStart);
    if (this.isNumeralChar(char)) {
      return selectionStart + prefixLength;
    }
    let i = selectionStart - 1;
    while (i >= 0) {
      char = inputValue.charAt(i);
      if (this.isNumeralChar(char)) {
        index = i + prefixLength;
        break;
      } else {
        i--;
      }
    }
    if (index !== null) {
      this.input.nativeElement.setSelectionRange(index + 1, index + 1);
    } else {
      i = selectionStart;
      while (i < valueLength) {
        char = inputValue.charAt(i);
        if (this.isNumeralChar(char)) {
          index = i + prefixLength;
          break;
        } else {
          i++;
        }
      }
      if (index !== null) {
        this.input.nativeElement.setSelectionRange(index, index);
      }
    }
    return index || 0;
  }

  onInputClick() {
    const currentValue = this.input.nativeElement.value;
    if (!this.readonly && currentValue !== window.getSelection()?.toString()) {
      this.initCursor();
    }
  }

  private isNumeralChar(char: string) {
    if (
      char.length === 1 &&
      (this._numeral.test(char) ||
        this._decimal.test(char) ||
        this._group.test(char) ||
        this._minusSign.test(char))
    ) {
      this.resetRegex();
      return true;
    }
    return false;
  }

  private resetRegex() {
    this._numeral.lastIndex = 0;
    this._decimal.lastIndex = 0;
    this._group.lastIndex = 0;
    this._minusSign.lastIndex = 0;
  }

  private updateValue(
    event: Event,
    valueStr: any,
    insertedValueStr: string | null,
    operation: string
  ) {
    const currentValue = this.input.nativeElement.value;
    if (valueStr != null) {
      const newValue = this.parseValue(valueStr);
      this.updateInput(newValue, insertedValueStr, operation, valueStr);
      this.handleOnInput(event, currentValue, newValue);
    }
  }

  private handleOnInput(event: Event, currentValue: any, newValue: any) {
    if (this.isValueChanged(currentValue, newValue)) {
      this.valueInput.emit({
        originalEvent: event,
        value: newValue,
        formattedValue: currentValue,
      });
    }
  }

  private isValueChanged(currentValue: any, newValue: any) {
    if (newValue === null && currentValue !== null) {
      return true;
    }
    if (newValue != null) {
      const parsedCurrentValue =
        typeof currentValue === 'string'
          ? this.parseValue(currentValue)
          : currentValue;
      return newValue !== parsedCurrentValue;
    }
    return false;
  }

  private validateValue(value: any) {
    if (value === '-' || value == null) {
      return null;
    }
    if (this.min != null && value < this.min) {
      return this.min;
    }
    if (this.max != null && value > this.max) {
      return this.max;
    }
    return value;
  }

  private updateInput(
    value: any,
    insertedValueStr: string | null,
    operation: string,
    valueStr: any
  ) {
    insertedValueStr = insertedValueStr || '';
    const inputValue = this.input.nativeElement.value;
    let newValue = this.formatValue(value);
    const currentLength = inputValue.length;
    if (newValue !== valueStr) {
      newValue = this.concatValues(newValue, valueStr);
    }
    if (currentLength === 0) {
      this.input.nativeElement.value = newValue;
      this.input.nativeElement.setSelectionRange(0, 0);
      const index = this.initCursor();
      const selectionEnd = index + insertedValueStr.length;
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else {
      const selectionStart = this.input.nativeElement.selectionStart as number;
      let selectionEnd = this.input.nativeElement.selectionEnd as number;
      this.input.nativeElement.value = newValue;
      const newLength = newValue.length;
      if (operation === 'range-insert') {
        const startValue = this.parseValue(
          (inputValue || '').slice(0, selectionStart)
        );
        const startValueStr = startValue !== null ? startValue.toString() : '';
        const startExpr = startValueStr.split('').join(`(${this.groupChar})?`);
        const sRegex = new RegExp(startExpr, 'g');
        sRegex.test(newValue);
        const tExpr = insertedValueStr.split('').join(`(${this.groupChar})?`);
        const tRegex = new RegExp(tExpr, 'g');
        tRegex.test(newValue.slice(sRegex.lastIndex));
        selectionEnd = sRegex.lastIndex + tRegex.lastIndex;
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      } else if (newLength === currentLength) {
        if (operation === 'insert' || operation === 'delete-back-single') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd + 1,
            selectionEnd + 1
          );
        } else if (operation === 'delete-single') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd - 1,
            selectionEnd - 1
          );
        } else if (operation === 'delete-range' || operation === 'spin') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd,
            selectionEnd
          );
        }
      } else if (operation === 'delete-back-single') {
        const prevChar = inputValue.charAt(selectionEnd - 1);
        const nextChar = inputValue.charAt(selectionEnd);
        const diff = currentLength - newLength;
        const isGroupChar = this._group.test(nextChar);
        if (isGroupChar && diff === 1) {
          selectionEnd += 1;
        } else if (!isGroupChar && this.isNumeralChar(prevChar)) {
          selectionEnd += -1 * diff + 1;
        }
        this._group.lastIndex = 0;
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      } else if (inputValue === '-' && operation === 'insert') {
        this.input.nativeElement.setSelectionRange(0, 0);
        const index = this.initCursor();
        const end = index + insertedValueStr.length + 1;
        this.input.nativeElement.setSelectionRange(end, end);
      } else {
        selectionEnd = selectionEnd + (newLength - currentLength);
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      }
    }
  }

  private concatValues(val1: string, val2: any) {
    if (val1 && val2) {
      const decimalCharIndex = val2.search(this._decimal);
      this._decimal.lastIndex = 0;
      if (this.suffixChar) {
        return (
          val1.replace(this.suffixChar, '').split(this._decimal)[0] +
          val2.replace(this.suffixChar, '').slice(decimalCharIndex) +
          this.suffixChar
        );
      }
      return decimalCharIndex !== -1
        ? val1.split(this._decimal)[0] + val2.slice(decimalCharIndex)
        : val1;
    }
    return val1;
  }

  private getDecimalLength(value: string) {
    if (value) {
      const valueSplit = value.split(this._decimal);
      if (valueSplit.length === 2) {
        return valueSplit[1]
          .replace(this._suffix, '')
          .trim()
          .replace(/\s/g, '')
          .replace(this._currency, '').length;
      }
    }
    return 0;
  }

  onInputBlur(event: Event) {
    this.focused = false;
    const newValue = this.validateValue(
      this.parseValue(this.input.nativeElement.value)
    );
    this.input.nativeElement.value = this.formatValue(newValue);
    this.updateModel(event, newValue);
  }

  formattedValue() {
    return this.formatValue(this.value);
  }

  private updateModel(event: Event, value: any) {
    if (this.value !== value) {
      this.value = value;
      this.onModelChange(value);
    }
    this.onModelTouched();
  }

  writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) {
      this.focused = false;
      this.clearTimer();
    }
    this.cd.markForCheck();
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
